import {
  ExpiryType,
  formatDateToRequestDay,
  InventoryManagementMethod,
  isListedInstrumentTradableInApp,
  TradeType,
  TradingSession,
} from '@eih-netbroker/common';
import { Collapse } from '@mui/material';
import { isWeekend } from 'date-fns';
import { memo, useCallback, useMemo, useState } from 'react';
import { ReactComponent as ArrowDownIcon } from '../../../../../assets/icons/arrow-down-s-line.svg';
import { ReactComponent as CalendarIcon } from '../../../../../assets/icons/calendar-line.svg';
import { useLocale } from '../../../../../context/NbLocalizationContext';
import { NbDatePicker } from '../../../../../shared/components/date-picker/DatePicker';
import { NbSelect } from '../../../../../shared/components/select/Select';
import { useNonTradingDays } from '../../../../../shared/services/useNonTradingDays';
import { useOptions } from '../../../../../shared/services/useOptions';
import { cssWhen } from '../../../../../shared/utils/themeUtils';
import { OrderFormData } from './OrderForm2';
import classes from './OrderFormAdditionalSettings.module.scss';

type OrderFormAdditionalSettingsProps = {
  order: OrderFormData;
  onChange: <T extends keyof OrderFormData>(key: T, value: OrderFormData[T]) => void;
  isLoading: boolean;
  disabled: boolean;
};

const SelectIcon = (props: React.SVGProps<SVGSVGElement>) => <ArrowDownIcon {...props} />;
const formatSelectOptionTestIdValue = (value: unknown): string => String(value).trim().toLowerCase();

export const OrderFormAdditionalSettings = memo(
  ({ order, onChange, isLoading, disabled }: OrderFormAdditionalSettingsProps) => {
    const { translation } = useLocale();
    const { getExpiryTypeOptions, getTradingSessionOptions, inventoryManagementMethodOptions } = useOptions();
    const { getNonTradingDaysByMic, getNextTradingDay } = useNonTradingDays();
    const [isOpen, setIsOpen] = useState(false);

    const nextTradingDay = useMemo(
      () => (order.instrument?.mic ? getNextTradingDay(order.instrument.mic) : undefined),
      [order.instrument, getNextTradingDay],
    );

    const shouldDisableDate = useCallback(
      (day: Date) => {
        if (!order.instrument) {
          return false;
        }
        const days = new Set(getNonTradingDaysByMic(order.instrument.mic));
        return isWeekend(day) || days.has(formatDateToRequestDay(day));
      },
      [getNonTradingDaysByMic, order],
    );

    const showDatePicker =
      order.instrument !== undefined && order.instrument !== null && order.expiryType === ExpiryType.Gtd;

    const tradeable = isListedInstrumentTradableInApp(order?.instrument);
    const showInventory = order.tradeType === TradeType.Sell && tradeable;

    return (
      <div className={classes.container}>
        <button
          type="button"
          className={classes.header}
          onClick={() => setIsOpen(prev => !prev)}
          data-testid="order-form-additional-settings-toggle"
        >
          <span className={classes.headerTitle}>{translation.orderModal.additionalSettings}</span>
          <ArrowDownIcon className={cssWhen(classes.headerIcon, [isOpen, classes.headerIconOpen])} />
        </button>
        <Collapse in={isOpen}>
          <div className={classes.content}>
            <div className={showInventory ? classes.fieldsThreeCol : classes.fieldsTwoCol}>
              <div className={classes.field}>
                <span
                  data-testid="order-form-time-in-force-label"
                  className={classes.fieldLabel}
                >
                  {translation.orderModal.timeInForce}
                </span>
                <NbSelect
                  size="small"
                  className={classes.select}
                  onChange={e => onChange('expiryType', e.target.value as ExpiryType)}
                  value={order.expiryType ?? ''}
                  options={getExpiryTypeOptions(order.instrument, order.orderType)}
                  disabled={disabled}
                  readOnly={isLoading}
                  IconComponent={SelectIcon}
                  inputProps={{ 'data-testid': 'order-form-time-in-force-input' }}
                  MenuProps={{
                    MenuListProps: {
                      'data-testid': 'order-form-time-in-force-list',
                    },
                  }}
                  getOptionTestId={option =>
                    `order-form-time-in-force-option-${formatSelectOptionTestIdValue(option.id)}`
                  }
                />
                {order.expiryType === ExpiryType.Gtd && (
                  <Collapse in={showDatePicker}>
                    <NbDatePicker
                      shouldDisableDate={shouldDisableDate}
                      size="small"
                      onChange={date => onChange('expiry', date ?? undefined)}
                      value={order.expiry}
                      minDate={nextTradingDay}
                      disabled={disabled}
                      readOnly={isLoading}
                      slots={{ openPickerIcon: CalendarIcon }}
                    />
                  </Collapse>
                )}
              </div>
              <div className={classes.field}>
                <span
                  data-testid="order-form-trading-session-label"
                  className={classes.fieldLabel}
                >
                  {translation.orderModal.tradingRestrictions}
                </span>
                <NbSelect
                  size="small"
                  className={classes.select}
                  onChange={e => onChange('tradingSession', e.target.value as TradingSession)}
                  value={order.tradingSession ?? ''}
                  options={getTradingSessionOptions(order.instrument, order.orderType)}
                  disabled={disabled}
                  readOnly={isLoading}
                  IconComponent={SelectIcon}
                  inputProps={{ 'data-testid': 'order-form-trading-session-input' }}
                  MenuProps={{
                    MenuListProps: {
                      'data-testid': 'order-form-trading-session-list',
                    },
                  }}
                  getOptionTestId={option =>
                    `order-form-trading-session-option-${formatSelectOptionTestIdValue(option.id)}`
                  }
                />
              </div>
              {showInventory && (
                <div className={classes.field}>
                  <span
                    data-testid="order-form-stock-valuation-label"
                    className={classes.fieldLabel}
                  >
                    {translation.orderModal.stockValuation}
                  </span>
                  <NbSelect
                    size="small"
                    className={classes.select}
                    onChange={e => onChange('inventoryManagementMethod', e.target.value as InventoryManagementMethod)}
                    value={order.inventoryManagementMethod ?? ''}
                    options={inventoryManagementMethodOptions}
                    disabled={order.tradeType !== TradeType.Sell || disabled}
                    readOnly={isLoading}
                    IconComponent={SelectIcon}
                    inputProps={{ 'data-testid': 'order-form-stock-valuation-input' }}
                    MenuProps={{
                      MenuListProps: {
                        'data-testid': 'order-form-stock-valuation-list',
                      },
                    }}
                    getOptionTestId={option =>
                      `order-form-stock-valuation-option-${formatSelectOptionTestIdValue(option.id)}`
                    }
                  />
                </div>
              )}
            </div>
          </div>
        </Collapse>
      </div>
    );
  },
);
